import React from 'react';
import { useNavigate } from 'react-router-dom';
import { IconButton, Tooltip } from '@mui/material';
import { Brightness4, Brightness7 } from '@mui/icons-material';
import { useThemeMode } from '../context/ThemeContext';
import './LandingPage.css';
import vicLogo from '../assets/vic_logo.png';

const LandingPage = () => {
  const navigate = useNavigate();
  const { mode, toggleTheme } = useThemeMode();

  return (
    <div className="landing" data-theme={mode}>
      {/* Navbar */}
      <nav className="landing-nav">
        <div className="nav-brand">
          <span className="logo-icon"><img src={vicLogo} alt="VIC - Service Checker Logo" /></span>
          <span className="logo-text">Service Checker</span>
        </div>
        <div className="nav-actions">
          <Tooltip title={mode === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode'}>
            <IconButton onClick={toggleTheme} color="inherit" size="small" sx={{ mr: 1 }}>
              {mode === 'light' ? <Brightness4 /> : <Brightness7 />}
            </IconButton>
          </Tooltip>
          <button className="btn btn-outline btn-sm" onClick={() => navigate('/login')}>
            Sign In
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => navigate('/register')}>
            Get Started
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <span className="hero-badge">🚀 for Businesses</span>
          <h1 className="hero-title">
            Validate VAT Numbers <br />
            <span className="hero-highlight">Instantly & Accurately</span>
          </h1>
          <p className="hero-subtitle">
            Streamline your tax compliance with our powerful VAT validation service.
            Verify EU VAT numbers in real-time, automate checks, and stay compliant
            with ease — all through a single, reliable API.
          </p>
          <div className="hero-actions">
            <button className="btn btn-primary btn-lg" onClick={() => navigate('/register')}>
              Get Started Free
            </button>
            <button className="btn btn-outline btn-lg" onClick={() => navigate('/login')}>
              Sign In →
            </button>
          </div>
          <p className="hero-note">No credit card required · Free tier available</p>
        </div>
        <div className="hero-visual">
          <div className="hero-card">
            <div className="card-header">VAT Validation Result</div>
            <div className="card-row">
              <span className="card-label">VAT Number</span>
              <span className="card-value">DE123456789</span>
            </div>
            <div className="card-row">
              <span className="card-label">Status</span>
              <span className="card-status valid">✓ Valid</span>
            </div>
            <div className="card-row">
              <span className="card-label">Company</span>
              <span className="card-value">Beispiel GmbH</span>
            </div>
            <div className="card-row">
              <span className="card-label">Country</span>
              <span className="card-value">Germany 🇩🇪</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features" id="features">
        <div className="section-container">
          <h2 className="section-title">Why Choose VIC Service Checker?</h2>
          <p className="section-subtitle">
            Everything you need for seamless VAT number validation
          </p>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">⚡</div>
              <h3>Real-Time Validation</h3>
              <p>Instantly verify VAT numbers against official EU VIES databases with sub-second response times.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🔒</div>
              <h3>Secure & Compliant</h3>
              <p>Enterprise-grade security with encrypted data transmission. Fully GDPR compliant.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🌍</div>
              <h3>EU-Wide Coverage</h3>
              <p>Support for all 27 EU member states plus Northern Ireland. One API, all countries.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📊</div>
              <h3>Detailed Reports</h3>
              <p>Access validation history, analytics dashboards, and exportable compliance reports.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🔌</div>
              <h3>Easy Integration</h3>
              <p>RESTful API with SDKs for JavaScript, Python, and more. Up and running in minutes.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📦</div>
              <h3>Bulk Validation</h3>
              <p>Upload CSV files or use batch endpoints to validate thousands of VAT numbers at once.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works" id="how-it-works">
        <div className="section-container">
          <h2 className="section-title">How It Works</h2>
          <p className="section-subtitle">Get started in three simple steps</p>
          <div className="steps">
            <div className="step">
              <div className="step-number">1</div>
              <h3>Create an Account</h3>
              <p>Sign up for free and get your API key instantly. No credit card needed.</p>
            </div>
            <div className="step-divider" />
            <div className="step">
              <div className="step-number">2</div>
              <h3>Integrate the API</h3>
              <p>Use our simple REST API or SDKs to integrate VAT validation into your workflow.</p>
            </div>
            <div className="step-divider" />
            <div className="step">
              <div className="step-number">3</div>
              <h3>Validate & Comply</h3>
              <p>Start validating VAT numbers in real-time and maintain full tax compliance.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing" id="pricing">
        <div className="section-container">
          <h2 className="section-title">Simple, Transparent Pricing</h2>
          <p className="section-subtitle">Start free, scale as you grow</p>
          <div className="pricing-grid">
            <div className="pricing-card">
              <h3>Free</h3>
              <div className="price">€0<span>/month</span></div>
              <ul>
                <li>✓ 100 validations/month</li>
                <li>✓ Single API key</li>
                <li>✓ Basic dashboard</li>
                <li>✓ Email support</li>
              </ul>
              <button className="btn btn-outline btn-full" onClick={() => navigate('/register')}>
                Get Started
              </button>
            </div>
            <div className="pricing-card">
              <h3>Enterprise</h3>
              <div className="price">Custom</div>
              <ul>
                <li>✓ Unlimited validations</li>
                <li>✓ Dedicated infrastructure</li>
                <li>✓ SLA guarantee</li>
                <li>✓ Custom integrations</li>
                <li>✓ Dedicated support</li>
              </ul>
              <button className="btn btn-outline btn-full">Contact Sales</button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="cta-banner">
        <div className="section-container">
          <h2>Ready to use our services?</h2>
          <p>Create an account or sign in to start validating VAT numbers today.</p>
          <div className="cta-actions">
            <button className="btn btn-primary btn-lg" onClick={() => navigate('/register')}>
              Create Free Account
            </button>
            <button className="btn btn-outline-light btn-lg" onClick={() => navigate('/login')}>
              Sign In to Your Account
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="section-container">
          <div className="footer-content">
            <div className="footer-brand">
              <span className="logo-icon"><img src={vicLogo} alt="VIC - Service Checker Logo" /></span>
              <span className="logo-text">Service Checker</span>
            </div>
            <p className="footer-copy">© {new Date().getFullYear()} VIC - Service Checker. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
